import { useState, useEffect, useMemo } from 'react'
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import api from '@/services/api'

const PAGE_SIZE = 10

interface AuditLog {
  id: string
  user: string | { id: string; name: string; email: string; role: string }
  action: string
  resource: string
  createdAt: string
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function AuditLogTab() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    api.get('/audit', { signal: controller.signal })
      .then((resp) => setLogs(resp.data.data ?? resp.data ?? []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [])

  const getUserName = (user: AuditLog['user']) => {
    if (typeof user === 'object' && user !== null) {
      return user.name || user.email || 'Usuário'
    }
    return user || 'Usuário'
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return logs
    const q = search.toLowerCase()
    return logs.filter((l) => {
      const uName = typeof l.user === 'object' && l.user !== null ? (l.user.name || l.user.email || '') : (l.user || '')
      return (
        uName.toLowerCase().includes(q) ||
        l.action.toLowerCase().includes(q) ||
        l.resource.toLowerCase().includes(q)
      )
    })
  }, [search, logs])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <div className='glass-card p-0 overflow-hidden'>
      {/* Header */}
      <div className='px-4 py-3 border-b border-[var(--glass-border)]'>
        <h3 className='font-semibold text-[var(--font-color-h3)]' style={{ fontSize: 'var(--font-size-h3)' }}>
          Logs de Auditoria
        </h3>
      </div>

      {/* Search */}
      <div className='px-4 py-3 border-b border-[var(--glass-border)]'>
        <div className='relative max-w-sm'>
          <span className='absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]'>
            <Search size={14} />
          </span>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder='Buscar logs...'
            className='w-full bg-[var(--glass-bg)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] rounded-md pl-9 pr-3 py-2 text-[13px] outline-none focus:border-[var(--brand-primary)] transition-colors min-h-[44px]'
          />
        </div>
      </div>

      {/* Tabela */}
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Usuário', 'Ação', 'Recurso', 'Data'].map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider whitespace-nowrap'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className='px-4 py-8 text-center text-[var(--text-muted)] text-[13px]'>
                  <Loader2 size={18} className='inline animate-spin mr-2' />
                  Carregando logs...
                </td>
              </tr>
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={4} className='px-4 py-8 text-center text-[var(--text-muted)] text-[13px]'>
                  Nenhum log encontrado.
                </td>
              </tr>
            ) : (
              paginated.map((log) => (
                <tr
                  key={log.id}
                  className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors cursor-pointer'
                >
                  <td className='px-4 py-3'>
                    <span className='font-medium text-[var(--text-primary)] text-[13px]'>{getUserName(log.user)}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='text-[var(--text-primary)] text-[13px]'>{log.action}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='text-[var(--text-muted)] font-mono text-[13px]'>{log.resource}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <span className='text-[var(--text-muted)] text-[13px]'>{formatDate(log.createdAt)}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <div className='flex items-center justify-between px-4 py-3 border-t border-[var(--glass-border)]'>
        <span className='text-[var(--text-muted)] text-[12px]'>
          {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
        </span>
        <div className='flex items-center gap-1'>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className='p-1.5 rounded-md hover:bg-[var(--glass-bg)] text-[var(--text-muted)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
          >
            <ChevronLeft size={16} />
          </button>
          <span className='text-[var(--text-muted)] text-[12px] px-2'>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className='p-1.5 rounded-md hover:bg-[var(--glass-bg)] text-[var(--text-muted)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center'
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}
